import {
	SearchBox as HeadlessSearchBox,
	StandaloneSearchBox as HeadlessStandAloneSearchBox,
	SearchEngine
} from "@coveo/headless";
import parse from "html-react-parser";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import HeaderSearchContext from "components/header/context/HeaderContentContext";
import { ImageSearchMagnifyingGlass } from "components/utility/images";
import { ImageCloseX } from "components/utility/images/ImageCloseX";
import {
	CoveoHeadlessContext,
	standaloneSearchBoxStorageKey
} from "components/coveo-headless/types/coveo-headless-types";
import { launchAnalytics, registerSearchHub } from "components/coveo-headless/utils/initAnalytics";
import { eventMisfired } from "components/public-site/audience-sort/utility/audienceSortApiHandlers";

interface PublicSearchboxProps {
	controller:
		| HeadlessSearchBox
		| HeadlessStandAloneSearchBox
		| { state; subscribe; updateText; selectSuggestion; submit; clear };
	context?: CoveoHeadlessContext;
	engine?: SearchEngine;
	searchHub?: string;
}

export const PublicSearchBox = ({
	controller,
	context,
	engine,
	searchHub
}: PublicSearchboxProps) => {
	const standAloneSearchBoxRef = useRef<HTMLDivElement | null>(null);
	const standaLoneSearchBoxBackdropRef = useRef<HTMLDivElement | null>(null);
	const standAloneSearchInputRef = useRef<HTMLInputElement | null>(null);

	const { openSearch, setOpenSearch } = useContext(HeaderSearchContext);
	const [state, setState] = useState(controller.state || { suggestions: [] });
	const [cursor, setCursor] = useState(-1);
	const [inputValue, setInputValue] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (process.env.NODE_ENV === "development") {
			setState({
				suggestions: [],
				value: inputValue
			});
		} else {
			controller.subscribe(() => {
				setState(controller.state);
			});
		}
	}, [controller, inputValue]);

	useEffect(() => {
		const searchBox = standAloneSearchBoxRef.current as HTMLDivElement;

		const handleScroll = () => {
			setOpenSearch!(false);
		};

		const handleClickOutside = (event: MouseEvent) => {
			const searchButton = document.querySelector(".header-public-site-search-button");
			if (
				(!standAloneSearchBoxRef.current?.contains(event.target as Node) &&
					!searchButton?.contains(event.target as Node)) ||
				standaLoneSearchBoxBackdropRef.current?.contains(event.target as Node)
			) {
				setOpenSearch!(false);
				window.removeEventListener("click", handleClickOutside);
			}
		};

		if (openSearch) {
			window.addEventListener("click", handleClickOutside);
			window.addEventListener("scroll", handleScroll);

			standAloneSearchInputRef.current?.focus();
			searchBox.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 1, fill: "forwards" });
		}
		return () => {
			window.removeEventListener("click", handleClickOutside);
			window.removeEventListener("scroll", handleScroll);
		};
	}, [setOpenSearch, openSearch]);

	const handleSelectSuggestion = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>,
			value: string
		) => {
			if (eventMisfired(event)) return;
			controller.selectSuggestion(value);
			setOpenSearch!(false);
			standAloneSearchInputRef.current?.blur();
		},
		[controller, setOpenSearch]
	);

	if (engine && context && searchHub) {
		launchAnalytics(engine, context);
		registerSearchHub(engine, searchHub);

		if ("redirectTo" in state && state.redirectTo) {
			const { redirectTo, value, analytics } = state;
			const data = JSON.stringify({ value, analytics });
			localStorage.setItem(standaloneSearchBoxStorageKey, data);

			window.location.href = value ? `${redirectTo}#q=${value}` : redirectTo;

			return null;
		}
	}

	const limitedSuggestions = state.suggestions.slice(0, 6);

	return openSearch ? (
		<div
			className="public-searchbox-container"
			ref={standAloneSearchBoxRef}
			data-qa="public-search-searchbox-container"
		>
			<div className="public-searchbox-backdrop" ref={standaLoneSearchBoxBackdropRef} />
			<div className="public-searchbox">
				<div className="public-searchbox-magnifying-glass">
					<ImageSearchMagnifyingGlass />
				</div>
				<input
					ref={standAloneSearchInputRef}
					className="public-searchbox-input t-body-lg"
					data-qa="public-search-input"
					placeholder={"Search for funds, articles, news, and more"}
					type="text"
					value={state.value}
					onChange={(e) => {
						if (process.env.NODE_ENV === "development") {
							setState({
								suggestions: e.target.value
									? [
											{
												rawValue: "value",
												highlightedValue: "<span>va</span>lue"
											},
											{
												rawValue: "value vs growth",
												highlightedValue: "<span>va</span>lue vs growth"
											},
											{
												rawValue: "value of advice",
												highlightedValue: "<span>va</span>lue of advice"
											},
											{
												rawValue: "value's rebound rewarded investors who stayed in their seats",
												highlightedValue:
													"<span>va</span>lue's rebound rewarded investors who stayed in their seats"
											},
											{
												rawValue: "valuation equation",
												highlightedValue: "<span>va</span>luation equation"
											}
										]
									: [],
								value: e.target.value
							});
						} else {
							controller.updateText(e.target.value);
						}
						setCursor(-1);
						setInputValue(undefined);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							if (inputValue) controller.selectSuggestion(inputValue);
							if (standAloneSearchInputRef.current?.value) controller.submit();
							if (inputValue || standAloneSearchInputRef.current?.value) {
								setOpenSearch!(false);
								standAloneSearchInputRef.current?.blur();
							}
						}
						if (e.key === "ArrowDown") {
							if (limitedSuggestions) {
								setCursor((prevCursor) =>
									prevCursor < limitedSuggestions.length - 1
										? prevCursor + 1
										: limitedSuggestions.length - 1 - prevCursor
								);
								setInputValue(
									limitedSuggestions[cursor === limitedSuggestions.length - 1 ? 0 : cursor + 1]
										.rawValue
								);
							}
						}
						if (e.key === "ArrowUp") {
							if (limitedSuggestions) {
								setCursor((prevCursor) =>
									prevCursor >= 0
										? prevCursor === 0
											? limitedSuggestions.length - 1 + prevCursor
											: prevCursor - 1
										: limitedSuggestions.length + prevCursor
								);
								setInputValue(
									limitedSuggestions[
										cursor === -1 || cursor === 0 ? limitedSuggestions.length - 1 : cursor - 1
									].rawValue
								);
							}
						}
					}}
				/>
				<button
					className="public-searchbox-close-button"
					data-qa="public-search-close-button"
					onClick={() => {
						controller.clear();
						standAloneSearchInputRef.current?.focus();
					}}
				>
					<ImageCloseX />
				</button>
				{state.value !== "" && openSearch && limitedSuggestions.length > 0 && (
					<div className="public-searchbox-suggestions">
						<div
							className="public-searchbox-suggestions-list"
							role="listbox"
							data-qa="public-search-suggestions-list"
						>
							{limitedSuggestions.map((suggestion, i) => {
								const value = suggestion.rawValue;
								return (
									<div
										key={value}
										aria-label={value}
										aria-selected={cursor === i}
										className={`public-searchbox-suggestion-item t-body-sm ${
											cursor === i ? "keySelected" : ""
										}`}
										role="option"
										tabIndex={0}
										onClick={(event) => handleSelectSuggestion(event, value)}
										onKeyDown={(event) => handleSelectSuggestion(event, value)}
									>
										{parse(suggestion.highlightedValue)}
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	) : null;
};
